// src/pages/AdminUsers.js
import { useState, useEffect, useCallback } from 'react';
import { Search, Users, TrendingUp, UserCheck, Building2, X, Shield, Crown, Clock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { admin } from '../services/api';

function TierBadge({ tier }) {
  return (
    <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold"
      style={{
        background:      tier === 'pro' ? 'linear-gradient(135deg, #00BCD4, #4CAF50)' : undefined,
        backgroundColor: tier !== 'pro' ? '#f1f5f9' : undefined,
        color:           tier === 'pro' ? '#fff' : '#64748b',
      }}>
      {tier}
    </span>
  );
}

// ─── Manage Tier Modal ─────────────────────────────────────────────────────────
function ManageTierModal({ user, onClose, onSave }) {
  const theme = useTheme();
  const [tier,      setTier]      = useState(user.tier || 'free');
  const [expiresAt, setExpiresAt] = useState('');
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  // presets in months
  const PRESETS = [
    { label: '1 month',  months: 1 },
    { label: '3 months', months: 3 },
    { label: '6 months', months: 6 },
    { label: '1 year',   months: 12 },
    { label: 'No expiry', months: 0 },
  ];

  // init from existing expiry
  useEffect(() => {
    if (user.pro_expires_at) {
      // format to YYYY-MM-DD for the date input
      const d = new Date(user.pro_expires_at);
      if (!isNaN(d)) setExpiresAt(d.toISOString().slice(0, 10));
    }
  }, [user.pro_expires_at]);

  function applyPreset(months) {
    if (months === 0) { setExpiresAt(''); return; }
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    setExpiresAt(d.toISOString().slice(0, 10));
  }

  async function handleSave() {
    setSaving(true); setError('');
    // if free, no expiry needed
    const expiry = tier === 'pro' && expiresAt ? expiresAt : null;
    const res = await admin.setTier(user.id, tier, expiry);
    setSaving(false);
    if (res.success) onSave();
    else setError(res.error || 'Failed to update');
  }

  const isPro     = tier === 'pro';
  const PRIMARY   = '#00ACC1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-sm rounded-2xl p-6 shadow-xl"
        style={{ backgroundColor: theme.card }}>

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-sm font-bold" style={{ color: theme.text }}>Manage Subscription</p>
            <p className="text-xs mt-0.5 truncate max-w-[220px]" style={{ color: theme.textMuted }}>{user.display_name || user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: theme.textMuted }}>
            <X size={16} />
          </button>
        </div>

        {/* Tier toggle */}
        <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
          style={{ color: theme.textMuted }}>Tier</p>
        <div className="flex rounded-xl p-1 mb-4" style={{ backgroundColor: theme.background }}>
          {['free', 'pro'].map(t => (
            <button key={t}
              onClick={() => setTier(t)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all"
              style={{
                backgroundColor: tier === t ? theme.card : 'transparent',
                color: tier === t ? (t === 'pro' ? PRIMARY : theme.text) : theme.textMuted,
                boxShadow: tier === t ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              {t === 'pro' ? <Crown size={13} /> : <Users size={13} />}
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Pro expiry — only shown when pro */}
        {isPro && (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-2"
              style={{ color: theme.textMuted }}>Pro Expires</p>

            {/* Quick presets */}
            <div className="flex gap-1.5 flex-wrap mb-3">
              {PRESETS.map(p => {
                // check if this preset matches current value
                let active = false;
                if (p.months === 0 && !expiresAt) active = true;
                if (p.months > 0 && expiresAt) {
                  const target = new Date();
                  target.setMonth(target.getMonth() + p.months);
                  active = expiresAt === target.toISOString().slice(0, 10);
                }
                return (
                  <button key={p.label}
                    onClick={() => applyPreset(p.months)}
                    className="px-2.5 py-1.5 rounded-lg text-[10px] font-semibold transition-all"
                    style={{
                      border: `1px solid ${active ? PRIMARY : theme.border}`,
                      backgroundColor: active ? `${PRIMARY}15` : 'transparent',
                      color: active ? PRIMARY : theme.textMuted,
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            {/* Date picker */}
            <input type="date" value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border text-sm outline-none mb-1"
              style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}
            />
            <p className="text-[10px] mb-4" style={{ color: theme.textMuted }}>
              {expiresAt
                ? `Expires ${new Date(expiresAt + 'T23:59:59').toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`
                : 'No expiry — pro access indefinitely'}
            </p>
          </>
        )}

        {/* Error */}
        {error && (
          <div className="mb-4 px-3 py-2 rounded-xl text-xs border"
            style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium"
            style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignBatchModal({ user, batches, onClose, onSave }) {
  const theme    = useTheme();
  const [batchId, setBatchId] = useState(user.voucher_batch_id || '');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  async function handleSave() {
    setSaving(true); setError('');
    const res = await admin.assignBatch(user.id, batchId || null);
    setSaving(false);
    if (res.success) onSave();
    else setError(res.error || 'Failed to assign');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full max-w-sm rounded-2xl p-6 shadow-xl"
        style={{ backgroundColor: theme.card }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm font-semibold" style={{ color: theme.text }}>Assign Dealer Batch</p>
            <p className="text-xs mt-0.5 truncate max-w-[220px]" style={{ color: theme.textMuted }}>{user.email}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70" style={{ color: theme.textMuted }}>
            <X size={16} />
          </button>
        </div>
        {error && (
          <div className="mb-4 px-3 py-2 rounded-xl text-xs border"
            style={{ backgroundColor: theme.isDark ? '#3b1111' : '#fef2f2', color: '#f87171', borderColor: '#f87171' }}>
            {error}
          </div>
        )}
        <div className="mb-5">
          <label className="block text-xs font-medium mb-1.5" style={{ color: theme.textMuted }}>Voucher Batch</label>
          <select value={batchId} onChange={e => setBatchId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border text-sm outline-none"
            style={{ backgroundColor: theme.background, borderColor: theme.border, color: theme.text }}>
            <option value="">— None (remove access) —</option>
            {batches.map(b => (
              <option key={b.id} value={b.id}>{b.name} ({b.total_codes || 0} codes, {b.pro_months}mo)</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium"
            style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const theme   = useTheme();
  const [users,     setUsers]     = useState([]);
  const [stats,     setStats]     = useState({});
  const [batches,   setBatches]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [tier,      setTier]      = useState('');
  const [meta,      setMeta]      = useState({ page: 1, total: 0, pages: 1 });
  const [assigning, setAssigning] = useState(null);
  const [managing,  setManaging]  = useState(null);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    const params = { page };
    if (search) params.search = search;
    if (tier)   params.tier   = tier;
    const res = await admin.listUsers(params);
    setLoading(false);
    if (res.success) {
      setUsers(res.data.data  || []);
      setStats(res.data.stats || {});
      setMeta(res.data.meta   || {});
    }
  }, [search, tier]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { admin.listBatches().then(res => { if (res.success) setBatches(res.data || []); }); }, []);

  function handleAssignSaved() { setAssigning(null); load(meta.page); }
  function handleTierSaved()   { setManaging(null);  load(meta.page); }

  const inputStyle = { backgroundColor: theme.background, borderColor: theme.border, color: theme.text };

  return (
    <div className="space-y-4 pb-10">
      <h1 className="text-xl font-bold" style={{ color: theme.text }}>Users</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Users,      label: 'Total',    value: stats.total      || 0, color: theme.text },
          { icon: TrendingUp, label: 'Pro',       value: stats.pro_users  || 0, color: '#00ACC1' },
          { icon: UserCheck,  label: 'Free',      value: stats.free_users || 0, color: theme.textMuted },
          { icon: Users,      label: 'New Today', value: stats.new_today  || 0, color: '#4CAF50' },
        ].map(s => (
          <div key={s.label} className="rounded-2xl p-4"
            style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={13} style={{ color: s.color }} />
              <p className="text-[10px] font-medium uppercase tracking-wider" style={{ color: theme.textMuted }}>{s.label}</p>
            </div>
            <p className="text-xl font-bold" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <form onSubmit={e => { e.preventDefault(); load(1); }} className="flex gap-2 flex-1 min-w-[180px]">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search email or name..."
            className="flex-1 px-3 py-2 rounded-xl border text-sm outline-none"
            style={inputStyle} />
          <button type="submit" className="p-2 rounded-xl" style={{ backgroundColor: theme.card }}>
            <Search size={15} style={{ color: theme.textMuted }} />
          </button>
        </form>
        <div className="flex rounded-xl overflow-hidden border" style={{ borderColor: theme.border }}>
          {[{ val: '', label: 'All' }, { val: 'pro', label: 'Pro' }, { val: 'free', label: 'Free' }].map(t => (
            <button key={t.val} onClick={() => setTier(t.val)}
              className="px-3 py-2 text-xs font-medium transition-colors"
              style={{ backgroundColor: tier === t.val ? '#00ACC1' : theme.card, color: tier === t.val ? '#fff' : theme.textSecondary }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
        </div>
      ) : users.length === 0 ? (
        <div className="rounded-2xl p-8 text-center text-sm" style={{ backgroundColor: theme.card, color: theme.textMuted }}>No users found</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block rounded-2xl overflow-hidden"
            style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['User', 'Tier', 'Expires', 'Provider', 'Dealer Batch', 'Joined', ''].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const batch = batches.find(b => b.id === u.voucher_batch_id);
                  const expired = u.pro_expires_at && new Date(u.pro_expires_at) < new Date();
                  return (
                    <tr key={u.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td className="px-5 py-3">
                        <p className="text-sm font-medium" style={{ color: theme.text }}>{u.display_name || '—'}</p>
                        <p className="text-xs" style={{ color: theme.textMuted }}>{u.email}</p>
                      </td>
                      <td className="px-5 py-3"><TierBadge tier={u.tier} /></td>
                      <td className="px-5 py-3">
                        {u.tier === 'pro' && u.pro_expires_at ? (
                          <span className="text-xs font-medium" style={{ color: expired ? '#f87171' : theme.textMuted }}>
                            {new Date(u.pro_expires_at).toLocaleDateString('en-ZA')}
                            {expired && <span className="ml-1 text-[10px]">(expired)</span>}
                          </span>
                        ) : u.tier === 'pro' ? (
                          <span className="text-xs" style={{ color: '#4CAF50' }}>No expiry</span>
                        ) : (
                          <span className="text-xs" style={{ color: theme.textMuted }}>—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs capitalize" style={{ color: theme.textMuted }}>{u.auth_provider || 'email'}</td>
                      <td className="px-5 py-3">
                        {batch ? (
                          <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#00ACC1' }}>
                            <Building2 size={12} />{batch.name}
                          </span>
                        ) : <span className="text-xs" style={{ color: theme.textMuted }}>—</span>}
                      </td>
                      <td className="px-5 py-3 text-xs" style={{ color: theme.textMuted }}>
                        {new Date(u.created_at).toLocaleDateString('en-ZA')}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setManaging(u)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-opacity hover:opacity-75"
                            style={{ backgroundColor: '#00ACC118', color: '#00ACC1' }}>
                            <Crown size={12} />Manage
                          </button>
                          <button onClick={() => setAssigning(u)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-opacity hover:opacity-75"
                            style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
                            <Building2 size={12} />{u.voucher_batch_id ? 'Batch' : 'Batch'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {users.map(u => {
              const batch = batches.find(b => b.id === u.voucher_batch_id);
              const expired = u.pro_expires_at && new Date(u.pro_expires_at) < new Date();
              return (
                <div key={u.id} className="rounded-2xl p-4"
                  style={{ backgroundColor: theme.card, boxShadow: theme.isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.08)' }}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium" style={{ color: theme.text }}>{u.display_name || '—'}</p>
                      <p className="text-xs" style={{ color: theme.textMuted }}>{u.email}</p>
                    </div>
                    <TierBadge tier={u.tier} />
                  </div>
                  {u.tier === 'pro' && (
                    <p className="text-[10px] mb-2" style={{ color: expired ? '#f87171' : theme.textMuted }}>
                      {u.pro_expires_at
                        ? `Expires ${new Date(u.pro_expires_at).toLocaleDateString('en-ZA')}${expired ? ' (expired)' : ''}`
                        : 'No expiry'}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    {batch ? (
                      <span className="flex items-center gap-1.5 text-xs font-medium" style={{ color: '#00ACC1' }}>
                        <Building2 size={12} />{batch.name}
                      </span>
                    ) : <span className="text-xs" style={{ color: theme.textMuted }}>No batch</span>}
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setManaging(u)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ backgroundColor: '#00ACC118', color: '#00ACC1' }}>
                        <Crown size={12} />Manage
                      </button>
                      <button onClick={() => setAssigning(u)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
                        style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
                        <Building2 size={12} />Batch
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Pagination */}
      {meta.pages > 1 && (
        <div className="flex items-center gap-2 justify-center">
          <button onClick={() => load(meta.page - 1)} disabled={meta.page <= 1}
            className="px-4 py-2 rounded-xl text-xs font-medium border disabled:opacity-40"
            style={{ backgroundColor: theme.card, borderColor: theme.border, color: theme.text }}>Prev</button>
          <span className="text-xs" style={{ color: theme.textMuted }}>{meta.page} / {meta.pages}</span>
          <button onClick={() => load(meta.page + 1)} disabled={meta.page >= meta.pages}
            className="px-4 py-2 rounded-xl text-xs font-medium border disabled:opacity-40"
            style={{ backgroundColor: theme.card, borderColor: theme.border, color: theme.text }}>Next</button>
        </div>
      )}

      {assigning && (
        <AssignBatchModal user={assigning} batches={batches} onClose={() => setAssigning(null)} onSave={handleAssignSaved} />
      )}

      {managing && (
        <ManageTierModal user={managing} onClose={() => setManaging(null)} onSave={handleTierSaved} />
      )}
    </div>
  );
}